import { useEffect, useMemo, useRef, useState } from "react";
import { useConfigure, useInfiniteHits, useInstantSearch, useSearchBox, useSortBy } from "react-instantsearch";

import { ALGOLIA_RETRIEVED_ATTRIBUTES, getAlgoliaIndex, getAlgoliaReplica } from "@repo/shopify/algolia/client";
import { ALGOLIA_CONFIG } from "@repo/shopify/algolia/config";
import { getInfiniteHitsCache } from "@repo/shopify/algolia/infiniteHitsCache";
import { markAlgoliaDown } from "@repo/shopify/algolia/status";
import useSyncedFacet from "@repo/shopify/algolia/useSyncedFacet";
import { readVisibleCount, writeVisibleCount } from "@repo/shopify/algolia/visibleCount";
import type { IAlgoliaProductHit, IFacetItem } from "@repo/shopify/types/api/algolia";
import type { ISearchQuery } from "@repo/shopify/types/api/search";
import type { Filter } from "@repo/shopify/types/generated/storefront.types";

import { toProductCards, toProductGroups } from "@hooks/algolia/groupHits";

function toFilterValues(id: string, items: IFacetItem[], toLabel: (value: string) => string = (value) => value) {
  return items.map((item) => ({
    id: `${id}.${item.value}`,
    label: toLabel(item.value),
    count: item.count,
    input: JSON.stringify({ [id]: item.value }),
  }));
}

type TSortName = "relevance" | "price_descending" | "price_ascending";

const PRICE_REPLICA: Record<Exclude<TSortName, "relevance">, "priceDesc" | "priceAsc"> = {
  price_descending: "priceDesc",
  price_ascending: "priceAsc",
};

/** 상품 가격(Shopify 검색의 PRICE 정렬과 같게 상품 최저가 기준) */
const productPrice = (variants: IAlgoliaProductHit[]) => variants[0].variants_min_price ?? variants[0].price ?? 0;

/**
 * 검색 결과 페이지용 Algolia 훅.
 *
 * 기존 SearchResult 의 useQuery 와 같은 모양({ searchData, products, expandProducts, ... })을 돌려주므로
 * 화면 코드는 데이터 출처만 바꾸면 된다.
 */
export default function useAlgoliaSearchResult(queryParameter: string, searchParams: URLSearchParams) {
  const { refine: refineQuery } = useSearchBox();
  const { status, error } = useInstantSearch();
  // 캐시가 있어야 뒤로가기 시 첫 렌더부터 hits 가 그려진다 (스크롤 복원 조건)
  const { items } = useInfiniteHits({ cache: getInfiniteHitsCache("search") });

  // 카드 표시(products / expandProducts)는 화면이 고르고, 여기서는 페이지 크기만 본다.
  // 기존 화면 그대로 view === "seperate" 일 때만 12개씩이다.
  const pageSize = searchParams.get("view") === "seperate" ? 12 : 24;
  const sort = (searchParams.get("sort") ?? "relevance") as TSortName;

  // 정렬은 replica 인덱스 전환으로 처리한다. replica 가 없으면(설정이 비어 기본 인덱스가 돌아오면)
  // 기본 인덱스로 받은 결과를 화면에서 가격순으로 정렬한다. 매칭된 변형을 한 번에 받으므로 결과가 1000건 이하면 순서가 정확하다.
  const priceReplica = sort === "relevance" ? undefined : PRICE_REPLICA[sort];
  const sortOnClient = priceReplica !== undefined && getAlgoliaReplica(priceReplica) === getAlgoliaIndex();

  const sortIndexes = useMemo(
    () => [
      { label: "relevance", value: getAlgoliaIndex() },
      { label: "price_descending", value: getAlgoliaReplica("priceDesc") },
      { label: "price_ascending", value: getAlgoliaReplica("priceAsc") },
    ],
    [],
  );

  const { refine: refineSort } = useSortBy({ items: sortIndexes });

  // 인덱스 기본값(hitsPerPage 20)으로 받으면 상품이 10개 안팎에서 끝나고 색상칩이 잘린다.
  // 매칭된 변형을 한 번에 받아 화면에서 묶는 전제라 반드시 MAX_HITS 로 요청한다.
  useConfigure({
    hitsPerPage: 1000,
    filters: ALGOLIA_CONFIG.visibleProductsFilter,
    attributesToRetrieve: ALGOLIA_RETRIEVED_ATTRIBUTES,
    attributesToHighlight: [],
    attributesToSnippet: [],
  });

  const parseParam = (key: string) => {
    const raw = searchParams.get(key);
    if (!raw) return [];
    try {
      return JSON.parse(raw) as string[];
    } catch {
      return [];
    }
  };

  const selectedCategory = parseParam("category");
  const selectedColor = parseParam("color");
  const selectedSize = parseParam("size");

  // 선택된 필터를 문자열로 눌러 둬서 effect 의존성을 안정적으로 만든다.
  const selectedKey = [selectedCategory, selectedColor, selectedSize].map((values) => values.join(",")).join("|");

  const categoryItems = useSyncedFacet(ALGOLIA_CONFIG.facet.productType, selectedCategory);
  const colorItems = useSyncedFacet(ALGOLIA_CONFIG.facet.color, selectedColor);
  const sizeItems = useSyncedFacet(ALGOLIA_CONFIG.facet.size, selectedSize);

  useEffect(() => {
    refineQuery(queryParameter);
  }, [queryParameter]);

  useEffect(() => {
    refineSort(sortIndexes.find((item) => item.label === sort)?.value ?? getAlgoliaIndex());
  }, [sort]);

  // 검색어 · 필터 · 정렬 · 보기 방식이 바뀌면 다시 첫 페이지부터
  const stateKey = `${queryParameter}|${sort}|${pageSize}|${selectedKey}`;

  const [visibleCount, setVisibleCount] = useState(() => readVisibleCount(stateKey, pageSize));

  // 마운트 직후에는 복구한 값을 지켜야 하므로 조건이 "바뀐" 경우만 리셋한다
  const prevStateKeyRef = useRef(stateKey);

  useEffect(() => {
    if (prevStateKeyRef.current === stateKey) return;

    prevStateKeyRef.current = stateKey;
    setVisibleCount(pageSize);
    writeVisibleCount(stateKey, pageSize);
  }, [stateKey]);

  useEffect(() => {
    // 인덱스가 사라지는 등 요청이 실패하면 화면을 기존 Shopify 검색으로 되돌린다.
    if (error) markAlgoliaDown(error.message);
  }, [error]);

  // Gift / GWP 는 서버에서 걸러진다(VISIBLE_PRODUCTS_FILTER). hidden 은 toProductGroups 가 거른다.
  const productGroups = useMemo(() => {
    const groups = toProductGroups(items);
    if (!sortOnClient) return groups;

    const direction = sort === "price_ascending" ? 1 : -1;
    // 가격이 같으면 관련도 순서를 유지한다(Array.prototype.sort 는 안정 정렬).
    return [...groups].sort((a, b) => (productPrice(a) - productPrice(b)) * direction);
  }, [items, sortOnClient, sort]);

  const { products, expandProducts } = useMemo(() => toProductCards(productGroups.slice(0, visibleCount)), [productGroups, visibleCount]);

  const hasNextPage = visibleCount < productGroups.length;

  const filters = useMemo<Filter[]>(
    () =>
      [
        {
          id: "filter.p.product_type",
          label: "Category",
          type: "LIST",
          values: toFilterValues("filter.p.product_type", categoryItems),
        },
        {
          id: "filter.p.m.group.color",
          label: "Color",
          type: "LIST",
          values: toFilterValues("filter.p.m.group.color", colorItems),
        },
        {
          id: "filter.v.option.size",
          label: "Size",
          type: "LIST",
          values: toFilterValues("filter.v.option.size", sizeItems),
        },
        {
          // getTotalProductCount 가 id 에 "hidden" 이 든 필터의 No 개수를 전체 상품 수로 쓴다.
          id: "filter.p.m.operation.hidden",
          label: "Hidden",
          type: "BOOLEAN",
          values: [{ id: "filter.p.m.operation.hidden.false", label: "No", count: productGroups.length, input: "{}" }],
        },
      ] as Filter[],
    [categoryItems, colorItems, sizeItems, productGroups.length],
  );

  const loading = status === "loading" || status === "stalled";

  // 첫 응답 전에는 InstantSearch 가 빈 결과를 내보내므로, 보여줄 상품이 없는 동안은 데이터 없음으로 둔다.
  const searchData = useMemo(
    () =>
      productGroups.length === 0 && loading
        ? undefined
        : ({
            totalCount: productGroups.length,
            productFilters: filters,
            pageInfo: { hasNextPage, endCursor: "" },
          } as ISearchQuery["search"]),
    [productGroups.length, loading, filters, hasNextPage],
  );

  return {
    searchData,
    products,
    expandProducts,
    hasNextPage,
    loading,
    fetchMore: () => {
      const next = visibleCount + pageSize;

      setVisibleCount(next);
      writeVisibleCount(stateKey, next);
    },
  };
}
